import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getApplicationData,
  preVerifyApplication,
  verifyApplication,
  updateApplicationStatus,
  updateEmploymentData,
  updateSelectedTradelines,
  getApplicationApr,
  uploadDocuments,
  updateAdditionalFunds,
  updatePaymentMethod,
  setRecommendationStrategy,
  saveSelectedOffer,
  getAdditionalFunds,
  updateOfferWithVerifiedIncome,
  updatePayrollProviderToManual,
  sendDirectDepositEnrollmentFormEmail,
  validateIdentity,
  updateApplicationCurrentFlow,
} from 'thunks';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { CurrentFlow } from 'enums/CurrentFlow';
import { CompanyProduct } from 'enums/CompanyProduct';
import { UtmSource } from 'enums/UtmTagName';
import { VerificationResult, PreVerifyResult } from 'api/LoanOfferApi';
import { CardColor } from 'components/CardFlow/Customize/cardVersions';
import { VerificationStep, VerificationStepProgress } from 'components/Verification/verificationSteps';
import { UploadDocumentsResponse } from 'api/DocumentsApi';
import { ProfessionGroup } from 'enums/ProfessionGroup';
import { PayrollProviderName } from 'api/EmployersApi';
import { PaymentMethod } from 'enums/PaymentMethod';
import { PartnerId } from 'enums/PartnerName';
import { RecommendationStrategy } from 'api/ApplicationDataApi';

import { MethodCapability } from 'enums/MethodTypes';

import { LoanGoal } from './yourGoal';

import { ReferralProgram } from './cardData';

export interface HardOfferData {
  payOff: Tradeline[];
  keepIt: Tradeline[];
  cantConsolidate: Tradeline[];
  payOffTotal: HardOfferTotal;
  additionalFundsTotal: HardOfferTotal;
  loanTotal: HardOfferTotal;
  planneryLoan: HardOfferTotal;
  offerSummary: HardOfferSummary;
  discountSummary: DiscountSummary;
  manuallyUpdatedSelections: boolean;
  manuallyUpdatedAdditionalFunds: boolean;
}

export enum DebtRecommendation {
  KeepIt = 'keep-it',
  PayOff = 'pay-off',
}

export enum DebtName {
  PersonalLoan = 'PersonalLoan',
  CreditCard = 'CreditCard',
  StudentLoan = 'StudentLoan',
  AutoLoan = 'AutoLoan',
  Mortgage = 'Mortgage',
}

export interface DebtSummary {
  [LoanType.PersonalLoan]: Debt;
  [LoanType.CreditCard]: Debt;
  [LoanType.StudentLoan]: Debt;
  [LoanType.AutoLoan]: Debt;
  [LoanType.Mortgage]: Debt;
  [LoanType.Other]: Debt;
  [LoanType.MedicalLoan]: Debt;
}

export interface Debt {
  name: DebtName;
  totalBalance: number | null;
  totalConsolidatableBalance: number | null;
  apr: number | null;
  recommendation: DebtRecommendation;
  tradelines: Tradeline[];
  cardOffer?: CardOffer;
}

interface CardOffer {
  apr: number;
  totalInterest: number;
  primaryCardId: string;
}

export interface HardOfferTotal {
  amount: number;
  apr: number;
  term: number;
  financeCharge: number;
  monthlyPayment: number;
}

export interface HardOfferSummary {
  loanAmount: number;
  moneySaved?: number;
  monthsSaved?: number;
  monthlyPaymentSaved?: number;
}

export interface DiscountSummary {
  baseApr: number;
  baseInterestRate: number;
  baseOriginationFee: number;
  items: DiscountItem[];
}

export enum DiscountType {
  Tenure = 'Tenure',
  Partner = 'Partner',
  Tier = 'Tier',
  // eslint-disable-next-line @typescript-eslint/no-shadow
  PaymentMethod = 'PaymentMethod',
  Promo = 'Promo',
}

export interface PartnerReason {
  partnerId: PartnerId;
}

export interface TenureReason {
  tenureYears: number;
  employerName?: string;
}

export interface TierReason {
  credentials?: string;
}

export interface PromoReason {
  promoText: string;
}

export interface DiscountItem {
  type: DiscountType;
  aprDifference: number;
  aprDifferencePercent: number;
  interestRateDifference: number;
  interestRateDifferencePercent: number;
  originationFeeDiscount: number;
  reason: PartnerReason | TenureReason | TierReason | PromoReason;
}

export enum LoanType {
  PersonalLoan = 'personal-loan',
  CreditCard = 'credit-card',
  MedicalLoan = 'medical-loan',
  StudentLoan = 'student-loan',
  AutoLoan = 'auto-loan',
  Mortgage = 'mortgage',
  Other = 'other',
}

export enum LoanTypeHumanized {
  PersonalLoan = 'Personal Loans',
  CreditCard = 'Credit Cards',
  MedicalLoan = 'Medical Loans',
  StudentLoan = 'Student Loans',
  AutoLoan = 'Car Loans',
  Mortgage = 'Mortgages',
  Other = 'Others',
}

export enum Recommendation {
  PayOff = 'PayOff',
  KeepIt = 'KeepIt',
  CantConsolidate = 'CantConsolidate',
}

export enum TradeLineStatus {
  Open = 'open',
  Closed = 'closed',
  Unknown = 'unknown',
}

export enum TradeLineErrCode {
  MissingTerm = 'missing-term',
  MissingBalance = 'missing-balance',
  MissingMonthlyPayment = 'missing-monthly-payment',

  EstimatedApr = 'estimated-apr',
  AprOutOfRange = 'apr-out-of-range',

  CantCalculateCreditCardTerm = 'cant-calculate-credit-card-term',
  CreditCardTermOufOfRange = 'credit-card-term-out-of-range',
}

export enum Source {
  CBC = 'CBC',
  Method = 'Method',
  CBCAndMethod = 'CBC and Method',
  Manual = 'Manual',
}
export interface Tradeline {
  id: string;
  firm: string;
  opened: string | null;
  originalBalance: number | null;
  balance: number;
  balanceToConsolidate: number;
  monthlyPayment: number | null;
  loanType: LoanType;
  term: number | null;
  status: TradeLineStatus;
  apr: number;
  recommendation: Recommendation;
  selectedForConsolidation: boolean | null;

  errorCode: string[] | null;
  methodCapabilities: MethodCapability[] | null;
  accountMask?: string | null;
  elapsedMonths?: number | null;
  remainingMonths?: number | null;
  partialInterest?: number | null;
  totalInterest?: number | null;
  planneryInterest?: number | null;
  savings?: number | null;
  monthlyPaymentSavings?: number | null;

  methodAccountId?: string;
  logoUrl?: string;
  weWillPay?: boolean;

  source: Source | null;
  userOverriddenBalanceToConsolidate: number | null;
}

export interface TradeLinesReport {
  proposed: Tradeline[];
  current: Tradeline[];
  other: Tradeline[];
}

export interface CreateApplicationData {
  status: ApplicationStatusName;
  product: CompanyProduct;
  borrowerFirstName: string;
  borrowerLastName: string;
  borrowerStreetAddress: string;
  borrowerCity: string;
  borrowerStateOrProvince: string;
  borrowerZipOrPostalCode: string;
  borrowerDateOfBirth: string;
  borrowerEmail: string;
  phoneNumber: string;
  loanAmountRange: string;
  creditScoreRange: string;
  authData?: any;
}

export interface GetApplicationData {
  id: string;
  status: ApplicationStatusName;
  createdAt: string;
  offerExpirationDate: string;
  currentFlow: CurrentFlow;
  previousFlow?: CurrentFlow;
  borrowerId: string;
  borrowerCitizenship?: string;
  borrowerFirstName?: string;
  borrowerLastName?: string;
  borrowerStreetAddress?: string;
  borrowerCity?: string;
  borrowerStateOrProvince?: string;
  borrowerZipOrPostalCode?: string;
  borrowerLast4SSN?: string;
  borrowerCredentials?: string;
  borrowerPersonalIdNumberVerificationAttempted: boolean;
  calculatedLoanAmount?: number;
  interestRate?: number;
  loanTermInMonths?: number;
  amountPerPaycheck?: number;
  loanAmount?: number;
  loanConsolidationAmount?: number;
  loanSelectedTerm?: number;
  maxLoanAmount: number;
  paymentMethod?: PaymentMethod;
  minLoanAmount?: number;
  firstDeductionDate?: string;
  monthlyLoanPayment?: number;
  borrowerEmail?: string;
  originationFee?: number;
  documentaryStampTaxFee?: number;
  plaidTokenLastUpdated?: string;
  bankName?: string;
  accountMask?: string;
  dateOfLoan?: string;
  feedbackExperience?: string;
  feedbackHowDidYouHear?: string;
  hardOffer?: string | HardOfferData;
  additionalFundsEligibility: boolean;
  canSwitchPaymentMethods: boolean;
  recommendationStrategy?: RecommendationStrategy;
  debtSummary?: string | DebtSummary;
  utmSource?: UtmSource;
  cardApplied?: boolean;
  cardReferralLink?: string;
  cardWaitListPosition?: number;
  cardOriginalWaitListPosition?: number;
  referralProgram?: ReferralProgram;
  cardColor?: CardColor;
  notEmployed?: boolean;
  additionalIncome?: number;
  employment: {
    employer1: Employer;
    employer2: Employer;
    isLicensedProfessional: boolean;
    professionGroup: ProfessionGroup;
    healthcareLicenseNumber?: string;
    healthcareLicenseState?: string;
  };
  totalAnnualIncome?: number;
  manualDeductionEmailSent?: boolean;
  completedRepaymentAccountStep?: boolean;
  fullDepositSwitch?: boolean;
  creditScore?: number;
  optimalPathPlan?: {
    scoreImprovement?: number;
  };
  frozenCreditScore?: boolean;
  kycPending?: boolean;
  kycStatus?: string;
  crsSmfaVerificationRequired?: boolean;
  crsNoFileReturned?: boolean;
  hasSignedACHForms?: boolean;
  missedPaymentReason?: string;
  missedPaymentAmount?: number;
  missedPaymentDate?: string;
  lateFee?: number;
  verificationStepProgress?: VerificationStepProgress;
  partnerId?: string;
  studentLoanAssistanceAppointment?: StudentLoanAssistanceAppointment;
  loanGoal?: LoanGoal[];
  clinicalLicenseVerificationEnabled?: boolean;
  isArgyleDataAvailable: boolean;
  promo?: {
    code: string;
    name: string;
    description: string;
    bannerContent: string;
    disclaimerContent: string;
    validTo: string;
    isActive: boolean;
  };
}

export interface StudentLoanAssistanceAppointment {
  date: Date;
  rescheduleLink?: string;
  cancelLink?: string;
}

export interface EmployerPortalData {
  verified: boolean;
  phoneNumber?: string;
  email?: string;
  hrTeamName?: string;
  attentionRange?: string;
  portalURL?: string;
  userStep?: string;
}

export interface Employer {
  employerName?: string;
  payFrequency?: string;
  hireDate?: string;
  jobTitle?: string;
  annualIncome?: number;
  logo?: string;
  linkItemId?: string;
  payrollProvider?: PayrollProviderName;
  verifiedName?: VerifyStatus;
  verifiedAnnualIncome?: VerifyStatus;
  verifiedHireDate?: VerifyStatus;
  employerPortalData?: EmployerPortalData;
  verifiedAnnualIncomeAmount?: number;
}

export enum VerifyStatus {
  Verified = 'Verified',
  Pending = 'Pending',
  NotVerified = 'NotVerified',
}

export interface ApplicationStatusUpdateResponse {
  applicationId: string;
  applicationStatus?: ApplicationStatusName;
  applicationFlow?: CurrentFlow;
}

export interface ApplicationUpdateCitizenshipResponse {
  citizenship: string;
}

export interface AdditionalFundsData {
  isEligible: boolean;
  maxAmount?: number;
}

interface ResumeProcessApplicationData {
  application?: GetApplicationData;
  apr?: number;
  currentVerificationStep?: VerificationStep;
  isLoading: boolean;
  isApplicationStatusUpdating: boolean;
  isAprLoading: boolean;
  isAdditionalFundsLoading: boolean;
  additionalFundsData?: AdditionalFundsData;
  preVerifyData?: PreVerifyResult;
  isPreVerifyLoading: boolean;
  paystubsUploaded: boolean;
}

export interface CreateApplicationResponse {
  passed: boolean;
  data: {
    application_id: string;
  };
}

export interface GetApplicationDataResponse {
  application: GetApplicationData;
}

export interface GetApplicationAprResponse {
  apr: number;
}

const initialState: ResumeProcessApplicationData = {
  isLoading: false,
  isApplicationStatusUpdating: false,
  isAprLoading: false,
  isAdditionalFundsLoading: false,
  isPreVerifyLoading: false,
  paystubsUploaded: false,
};

const applicationData = createSlice({
  name: 'applicationData',
  initialState,
  reducers: {
    setCurrentVerificationStep: (state, { payload }: PayloadAction<VerificationStep>) => {
      state.currentVerificationStep = payload;
    },
    completeVerificationStep: (state, { payload }: PayloadAction<VerificationStep>) => {
      state.application!.verificationStepProgress![payload] = true;
    },
    clearAdditionalFundsData: (state) => {
      state.additionalFundsData = undefined;
    },
    setPaystubsUploaded: (state, { payload }: PayloadAction<boolean>) => {
      state.paystubsUploaded = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getApplicationData.fulfilled, (state, { payload }: PayloadAction<GetApplicationDataResponse>) => {
      state.application = parseApplication(payload.application);
      state.isLoading = false;
    });
    builder.addCase(updateApplicationCurrentFlow.fulfilled, (state, { payload }) => {
      if (state.application) {
        state.application.currentFlow = payload;
      }
    });
    builder.addCase(validateIdentity.fulfilled, (state, { payload }: PayloadAction<GetApplicationDataResponse>) => {
      if (payload?.application) state.application = parseApplication(payload.application);
    });
    builder.addCase(updateEmploymentData.fulfilled, (state, { payload }: PayloadAction<GetApplicationDataResponse>) => {
      state.application = parseApplication(payload.application);
    });
    builder.addCase(getApplicationData.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getApplicationData.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(updateApplicationStatus.pending, (state) => {
      state.isApplicationStatusUpdating = true;
    });
    builder.addCase(updateApplicationStatus.rejected, (state) => {
      state.isApplicationStatusUpdating = false;
    });
    builder.addCase(
      updateApplicationStatus.fulfilled,
      (state, { payload }: PayloadAction<ApplicationStatusUpdateResponse>) => {
        state.isApplicationStatusUpdating = false;
        if (payload.applicationStatus) {
          state.application!.status = payload.applicationStatus;
        }
      },
    );
    builder.addCase(updateSelectedTradelines.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateSelectedTradelines.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(
      updateSelectedTradelines.fulfilled,
      (state, { payload }: PayloadAction<GetApplicationDataResponse>) => {
        state.isLoading = false;
        if (payload) {
          state.application! = parseApplication(payload.application);
        }
      },
    );
    builder.addCase(setRecommendationStrategy.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(setRecommendationStrategy.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(
      setRecommendationStrategy.fulfilled,
      (state, { payload }: PayloadAction<GetApplicationDataResponse>) => {
        state.isLoading = false;
        if (payload) {
          state.application! = parseApplication(payload.application);
        }
      },
    );
    builder.addCase(saveSelectedOffer.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(saveSelectedOffer.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(saveSelectedOffer.fulfilled, (state, { payload }: PayloadAction<GetApplicationDataResponse>) => {
      state.isLoading = false;
      if (payload) {
        state.application! = parseApplication(payload.application);
      }
    });
    builder.addCase(updateAdditionalFunds.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateAdditionalFunds.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(
      updateAdditionalFunds.fulfilled,
      (state, { payload }: PayloadAction<GetApplicationDataResponse>) => {
        state.isLoading = false;
        if (payload) {
          state.application! = parseApplication(payload.application);
        }
      },
    );
    builder.addCase(updatePaymentMethod.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updatePaymentMethod.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(updatePaymentMethod.fulfilled, (state, { payload }: PayloadAction<GetApplicationDataResponse>) => {
      state.isLoading = false;
      if (payload) {
        state.application! = parseApplication(payload.application);
      }
    });
    builder.addCase(updateOfferWithVerifiedIncome.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateOfferWithVerifiedIncome.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(
      updateOfferWithVerifiedIncome.fulfilled,
      (state, { payload }: PayloadAction<GetApplicationDataResponse>) => {
        state.isLoading = false;
        if (payload && payload.application) {
          state.application = parseApplication(payload.application);
        }
      },
    );
    builder.addCase(verifyApplication.fulfilled, (state, { payload }: PayloadAction<VerificationResult>) => {
      if (payload.applicationStatus) {
        state.application!.status = payload.applicationStatus;
      }
    });
    builder.addCase(getApplicationApr.pending, (state) => {
      state.isAprLoading = true;
    });
    builder.addCase(getApplicationApr.rejected, (state) => {
      state.isAprLoading = false;
    });
    builder.addCase(getApplicationApr.fulfilled, (state, { payload }: PayloadAction<GetApplicationAprResponse>) => {
      state.isAprLoading = false;
      state.apr = payload.apr;
    });
    builder.addCase(uploadDocuments.fulfilled, (state, { payload }: PayloadAction<UploadDocumentsResponse>) => {
      state.application && (state.application.verificationStepProgress = payload.verificationStepProgress);
    });
    builder.addCase(getAdditionalFunds.pending, (state) => {
      state.isAdditionalFundsLoading = true;
    });
    builder.addCase(getAdditionalFunds.fulfilled, (state, { payload }: PayloadAction<AdditionalFundsData>) => {
      state.isAdditionalFundsLoading = false;
      state.additionalFundsData = payload;
    });
    builder.addCase(getAdditionalFunds.rejected, (state) => {
      state.isAdditionalFundsLoading = false;
    });
    builder.addCase(preVerifyApplication.pending, (state) => {
      state.isPreVerifyLoading = true;
    });
    builder.addCase(preVerifyApplication.fulfilled, (state, { payload }: PayloadAction<PreVerifyResult>) => {
      state.isPreVerifyLoading = false;
      state.preVerifyData = payload;
    });
    builder.addCase(preVerifyApplication.rejected, (state) => {
      state.isPreVerifyLoading = false;
    });
    builder.addCase(updatePayrollProviderToManual.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      updatePayrollProviderToManual.fulfilled,
      (state, { payload }: PayloadAction<GetApplicationDataResponse>) => {
        state.isLoading = false;
        state.application = parseApplication(payload.application);
      },
    );
    builder.addCase(updatePayrollProviderToManual.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(sendDirectDepositEnrollmentFormEmail.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      sendDirectDepositEnrollmentFormEmail.fulfilled,
      (state, { payload }: PayloadAction<GetApplicationDataResponse>) => {
        state.isLoading = false;
        state.application = parseApplication(payload.application);
      },
    );
    builder.addCase(sendDirectDepositEnrollmentFormEmail.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  setCurrentVerificationStep,
  completeVerificationStep,
  clearAdditionalFundsData,
  setPaystubsUploaded,
} = applicationData.actions;

export default applicationData.reducer;

const parseApplication = (application: GetApplicationData): GetApplicationData => ({
  ...application,
  hardOffer: application.hardOffer ? JSON.parse(application.hardOffer as string) : null,
  debtSummary: application.debtSummary ? JSON.parse(application.debtSummary as string) : null,
});
